<template>
  <div class="cobrancas-page">
    <div v-if="!boletosOperacionais" class="alert alert-info" role="status">Operações de boletos desabilitadas para esta escola. O histórico permanece disponível.</div>
    <div
      class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3"
    >
      <div>
        <h5 class="mb-1">
          Cobranças e Boletos
        </h5>

        <small class="text-muted">
          Consulte cobranças emitidas, seus estados e instrumentos bancários.
        </small>
      </div>

      <button
        type="button"
        class="btn btn-outline-secondary btn-sm"
        :disabled="operacaoEmAndamento"
        @click="carregarCobrancas"
      >
        <i class="bi bi-arrow-clockwise me-1"></i>
        Atualizar
      </button>
    </div>

    <div class="card shadow-sm mb-3">
      <div class="card-body">
        <div class="filters-grid">
          <div class="filter-wide">
            <label class="form-label">
              Busca
            </label>

            <input
              v-model.trim="filters.busca"
              type="search"
              class="form-control form-control-sm"
              placeholder="Aluno, pagador, contrato, nosso número ou ID"
            />
          </div>

          <div>
            <label class="form-label">
              Status
            </label>

            <select
              v-model="filters.status"
              class="form-select form-select-sm"
              @change="aplicarFiltros"
            >
              <option value="">
                Todos
              </option>

              <option
                v-for="option in statusOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>

          <div>
            <label class="form-label">
              Instrumento
            </label>

            <select
              v-model="filters.tipo_instrumento"
              class="form-select form-select-sm"
              @change="aplicarFiltros"
            >
              <option value="">
                Todos
              </option>

              <option value="BOLETO">
                Boleto
              </option>

              <option value="BOLETO_PIX">
                Boleto + PIX
              </option>
            </select>
          </div>

          <div>
            <label class="form-label">
              Conta financeira
            </label>

            <select
              v-model="filters.conta_financeira_id"
              class="form-select form-select-sm"
              @change="aplicarFiltros"
            >
              <option value="">
                Todas
              </option>

              <option
                v-for="conta in contas"
                :key="conta.id"
                :value="conta.id"
              >
                {{ conta.apelido }} — {{ conta.instituicao }}
              </option>
            </select>
          </div>

          <div>
            <label class="form-label">
              Vencimento inicial
            </label>

            <input
              v-model="filters.data_vencimento_inicio"
              type="date"
              class="form-control form-control-sm"
              @change="aplicarFiltros"
            />
          </div>

          <div>
            <label class="form-label">
              Vencimento final
            </label>

            <input
              v-model="filters.data_vencimento_fim"
              type="date"
              class="form-control form-control-sm"
              @change="aplicarFiltros"
            />
          </div>

          <div class="filter-actions">
            <button
              type="button"
              class="btn btn-secondary btn-sm"
              :disabled="loading"
              title="Limpar filtros"
              @click="limparFiltros"
            >
              <i class="bi bi-funnel"></i>
              Limpar
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="errorMessage"
      class="alert alert-danger"
      role="alert"
    >
      {{ errorMessage }}
    </div>

    <div
      v-if="podeOperar && itensRegistraveis.length"
      class="bulk-actions card shadow-sm mb-3"
    >
      <div
        class="card-body d-flex flex-wrap justify-content-between align-items-center gap-2 py-2"
      >
        <div class="small text-muted">
          <strong>{{ selecionados.length }}</strong>
          cobrança(s) selecionada(s) nesta página.
        </div>

        <button
          type="button"
          class="btn btn-primary btn-sm"
          :disabled="selecionados.length === 0 || operacaoEmAndamento"
          @click="registrarSelecionadas"
        >
          <span
            v-if="processandoLote"
            class="spinner-border spinner-border-sm me-1"
            role="status"
            aria-hidden="true"
          ></span>

          <i
            v-else
            class="bi bi-send-check me-1"
          ></i>

          {{
            processandoLote
              ? 'Registrando selecionadas...'
              : 'Registrar selecionadas'
          }}
        </button>
      </div>
    </div>

    <div class="card shadow-sm">
      <div class="card-body p-0">
        <div
          v-if="loading"
          class="py-5 text-center"
        >
          <div
            class="spinner-border spinner-border-sm text-primary"
            role="status"
          ></div>

          <div class="mt-2 text-muted">
            Carregando cobranças...
          </div>
        </div>

        <div
          v-else-if="items.length === 0"
          class="py-5 text-center text-muted"
        >
          <i class="bi bi-receipt fs-3 d-block mb-2"></i>
          Nenhuma cobrança encontrada.
        </div>

        <div
          v-else
          class="table-responsive"
        >
          <table class="table table-striped table-hover align-middle mb-0">
            <thead class="table-light">
              <tr>
                <th class="selection-column text-center">
                  <input
                    v-if="podeOperar"
                    type="checkbox"
                    class="form-check-input"
                    :checked="todosRegistraveisSelecionados"
                    :disabled="itensRegistraveis.length === 0 || operacaoEmAndamento"
                    title="Selecionar cobranças registráveis desta página"
                    @change="alternarSelecaoPagina"
                  />
                </th>

                <th>
                  <button
                    type="button"
                    class="sort-button"
                    @click="ordenar('id')"
                  >
                    ID
                    <span>{{ sortArrow('id') }}</span>
                  </button>
                </th>

                <th>
                  <button
                    type="button"
                    class="sort-button"
                    @click="ordenar('aluno_nome')"
                  >
                    Aluno / pagador
                    <span>{{ sortArrow('aluno_nome') }}</span>
                  </button>
                </th>

                <th>Contrato</th>
                <th>Conta</th>
                <th>Instrumento</th>

                <th>
                  <button
                    type="button"
                    class="sort-button"
                    @click="ordenar('status')"
                  >
                    Status
                    <span>{{ sortArrow('status') }}</span>
                  </button>
                </th>

                <th>
                  <button
                    type="button"
                    class="sort-button"
                    @click="ordenar('data_vencimento')"
                  >
                    Vencimento
                    <span>{{ sortArrow('data_vencimento') }}</span>
                  </button>
                </th>

                <th class="text-end">
                  <button
                    type="button"
                    class="sort-button ms-auto"
                    @click="ordenar('valor')"
                  >
                    Valor
                    <span>{{ sortArrow('valor') }}</span>
                  </button>
                </th>

                <th class="text-center">
                  Boleto
                </th>

                <th class="text-center">
                  Ações
                </th>
              </tr>
            </thead>

            <tbody>
              <tr
                v-for="item in items"
                :key="item.id"
              >
                <td class="selection-column text-center">
                  <input
                    v-if="podeOperar && podeRegistrarItem(item)"
                    v-model="selecionados"
                    type="checkbox"
                    class="form-check-input"
                    :value="Number(item.id)"
                    :disabled="operacaoEmAndamento"
                    :aria-label="`Selecionar cobrança #${item.id}`"
                  />
                </td>

                <td class="fw-semibold">
                  #{{ item.id }}
                </td>

                <td>
                  <div class="fw-semibold">
                    {{ item.aluno_nome || 'Aluno não identificado' }}
                  </div>

                  <small
                    v-if="
                      item.pagador_nome &&
                      item.pagador_nome !== item.aluno_nome
                    "
                    class="text-muted"
                  >
                    Pagador: {{ item.pagador_nome }}
                  </small>
                </td>

                <td>
                  <div>
                    {{ item.numero_contrato || '—' }}
                  </div>

                  <small
                    v-if="item.matricula_id"
                    class="text-muted"
                  >
                    Matrícula #{{ item.matricula_id }}
                  </small>
                </td>

                <td>
                  {{ item.conta_financeira_apelido || '—' }}
                </td>

                <td>
                  {{ formatInstrument(item.tipo_instrumento) }}
                </td>

                <td>
                  <span
                    class="status-badge"
                    :class="statusClass(item.status)"
                  >
                    {{ statusLabel(item.status) }}
                  </span>
                </td>

                <td>
                  {{ formatDate(item.data_vencimento_atual) }}
                </td>

                <td class="text-end text-nowrap">
                  {{ formatMoney(item.valor_atual) }}
                </td>

                <td class="text-center">
                  <i
                    v-if="item.boleto_disponivel"
                    class="bi bi-check-circle-fill text-success"
                    title="Boleto disponível"
                  ></i>

                  <span
                    v-else
                    class="text-muted"
                  >
                    —
                  </span>
                </td>

                <td class="text-center text-nowrap">
                  <button
                    v-if="podeOperar && podeRegistrarItem(item)"
                    type="button"
                    class="btn btn-link btn-sm p-1"
                    :disabled="operacaoEmAndamento"
                    :title="item.status === 'ERRO' ? 'Tentar registrar novamente' : 'Registrar cobrança'"
                    @click="registrarItem(item)"
                  >
                    <span
                      v-if="estaProcessando(item.id)"
                      class="spinner-border spinner-border-sm text-primary"
                      role="status"
                      aria-hidden="true"
                    ></span>

                    <i
                      v-else
                      class="bi bi-send-check-fill text-success"
                    ></i>
                  </button>

                  <button
                    type="button"
                    class="btn btn-link btn-sm p-1"
                    :disabled="processandoLote"
                    title="Detalhar cobrança"
                    @click="detalhar(item.id)"
                  >
                    <i class="bi bi-eye-fill text-primary"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </div>

    <div
      class="d-flex flex-wrap justify-content-between align-items-center gap-3 mt-3"
    >
      <div>
        Mostrando
        {{ startRow }}
        a
        {{ endRow }}
        de
        {{ paginacao.total_itens }}
        cobranças
      </div>

      <div class="d-flex align-items-center gap-2">
        <BFormSelect
          v-model="limitePorPagina"
          :options="pageOptions"
          size="sm"
          class="page-limit"
        />
      </div>

      <div class="d-flex align-items-center">
        <BPagination
          v-model="paginaAtual"
          :per-page="limitePorPagina"
          :total-rows="paginacao.total_itens"
          :disabled="loading"
          size="sm"
          limit="5"
          class="mb-0"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { useBoletosOperacionais } from '@/composables/useBoletosOperacionais';
import {
  computed,
  getCurrentInstance,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch
} from 'vue';

import {
  BFormSelect,
  BPagination
} from 'bootstrap-vue-next';

import {
  useRouter
} from 'vue-router';

import cobrancasApi from '@/services/cobrancas.api';
import axios from '@/services/axios';
import globalUtils from '@/utils/globalUtils';
import {
  usePermissions
} from '@/composables/usePermissions';

const router =
  useRouter();

const instance =
  getCurrentInstance();

const proxy =
  instance?.proxy;

const {
  fetchPermissions,
  hasPermission
} = usePermissions();

const loading = ref(false);
const processandoLote = ref(false);
const processandoIds = ref(new Set());
const selecionados = ref([]);
const errorMessage = ref('');
const items = ref([]);
const contas = ref([]);

const paginaAtual = ref(1);
const limitePorPagina = ref(20);

const paginacao =
  reactive({
    pagina: 1,
    limite: 20,
    total_itens: 0,
    total_paginas: 0
  });

const sortState =
  reactive({
    field:
      'data_vencimento',

    order:
      'desc'
  });

const filters =
  reactive({
    busca: '',
    status: '',
    tipo_instrumento: '',
    conta_financeira_id: '',
    data_vencimento_inicio: '',
    data_vencimento_fim: ''
  });

const pageOptions = [
  {
    value: 10,
    text: '10 / página'
  },
  {
    value: 15,
    text: '15 / página'
  },
  {
    value: 20,
    text: '20 / página'
  },
  {
    value: 25,
    text: '25 / página'
  },
  {
    value: 50,
    text: '50 / página'
  }
];

const statusOptions = [
  ['RASCUNHO', 'Rascunho'],
  ['REGISTRO_PENDENTE', 'Registro pendente'],
  ['REGISTRADA', 'Registrada'],
  ['REJEITADA', 'Rejeitada'],
  ['VENCIDA', 'Vencida'],
  ['ALTERACAO_PENDENTE', 'Alteração pendente'],
  ['BAIXA_PENDENTE', 'Baixa pendente'],
  ['BAIXADA', 'Baixada'],
  ['LIQUIDADA', 'Liquidada'],
  ['CREDITADA', 'Creditada'],
  ['ESTORNADA', 'Estornada'],
  ['CANCELADA', 'Cancelada'],
  ['SUBSTITUIDA', 'Substituída'],
  ['ERRO', 'Erro']
].map(
  ([value, label]) => ({
    value,
    label
  })
);

const STATUS_REGISTRAVEIS =
  new Set([
    'RASCUNHO',
    'ERRO'
  ]);

const boletosOperacionais = useBoletosOperacionais();
const podeOperar =
  computed(() =>
    hasPermission(
      'financeiro_cobrancas_operar'
    ) && boletosOperacionais.value
  );

const itensRegistraveis =
  computed(() =>
    items.value.filter(
      podeRegistrarItem
    )
  );

const todosRegistraveisSelecionados =
  computed(() => {
    if (!itensRegistraveis.value.length) {
      return false;
    }

    const selecionadosSet =
      new Set(
        selecionados.value.map(Number)
      );

    return itensRegistraveis.value.every(
      (item) =>
        selecionadosSet.has(
          Number(item.id)
        )
    );
  });

const operacaoEmAndamento =
  computed(() =>
    loading.value ||
    processandoLote.value ||
    processandoIds.value.size > 0
  );

const statusLabels =
  Object.fromEntries(
    statusOptions.map(
      (option) => [
        option.value,
        option.label
      ]
    )
  );

const startRow =
  computed(() => {
    if (
      paginacao.total_itens === 0
    ) {
      return 0;
    }

    return (
      (paginaAtual.value - 1) *
        limitePorPagina.value +
      1
    );
  });

const endRow =
  computed(() =>
    Math.min(
      paginaAtual.value *
        limitePorPagina.value,

      paginacao.total_itens
    )
  );

let requestSequence = 0;
let searchTimer = null;

function apiErrorMessage(
  error,
  fallback
) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback
  );
}

async function carregarContas() {
  try {
    const response =
      await axios.get(
        '/financeiro/contas-financeiras',
        {
          params: {
            limit: 'all',
            sort: 'apelido',
            order: 'asc'
          }
        }
      );

    const result =
      response.data?.data ??
      response.data;

    contas.value =
      Array.isArray(result)
        ? result
        : [];
  } catch (error) {
    console.error(
      'Erro ao carregar contas financeiras:',
      error
    );

    contas.value = [];
  }
}

async function carregarCobrancas() {
  const requestId =
    ++requestSequence;

  loading.value = true;
  errorMessage.value = '';

  try {
    const result =
      await cobrancasApi.listar({
        pagina:
          paginaAtual.value,

        limite:
          limitePorPagina.value,

        busca:
          filters.busca,

        status:
          filters.status,

        tipo_instrumento:
          filters.tipo_instrumento,

        conta_financeira_id:
          filters.conta_financeira_id,

        data_vencimento_inicio:
          filters.data_vencimento_inicio,

        data_vencimento_fim:
          filters.data_vencimento_fim,

        ordenar_por:
          sortState.field,

        ordem:
          sortState.order
      });

    if (
      requestId !==
      requestSequence
    ) {
      return;
    }

    items.value =
      result.data;

    const registraveisDaPagina =
      new Set(
        result.data
          .filter(
            podeRegistrarItem
          )
          .map((item) =>
            Number(item.id)
          )
      );

    selecionados.value =
      selecionados.value.filter(
        (id) =>
          registraveisDaPagina.has(
            Number(id)
          )
      );

    Object.assign(
      paginacao,
      result.paginacao
    );

    if (
      paginaAtual.value >
        Math.max(
          result.paginacao
            .total_paginas,
          1
        ) &&
      result.paginacao
        .total_itens > 0
    ) {
      paginaAtual.value =
        Math.max(
          result.paginacao
            .total_paginas,
          1
        );
    }
  } catch (error) {
    if (
      requestId !==
      requestSequence
    ) {
      return;
    }

    console.error(
      'Erro ao carregar cobranças:',
      error
    );

    items.value = [];

    Object.assign(
      paginacao,
      {
        pagina:
          paginaAtual.value,

        limite:
          limitePorPagina.value,

        total_itens: 0,
        total_paginas: 0
      }
    );

    errorMessage.value =
      apiErrorMessage(
        error,
        'Não foi possível carregar as cobranças.'
      );
  } finally {
    if (
      requestId ===
      requestSequence
    ) {
      loading.value = false;
    }
  }
}

function executarNaPrimeiraPagina() {
  if (paginaAtual.value !== 1) {
    paginaAtual.value = 1;
    return;
  }

  carregarCobrancas();
}

function aplicarFiltros() {
  executarNaPrimeiraPagina();
}

function limparFiltros() {
  Object.assign(
    filters,
    {
      busca: '',
      status: '',
      tipo_instrumento: '',
      conta_financeira_id: '',
      data_vencimento_inicio: '',
      data_vencimento_fim: ''
    }
  );

  Object.assign(
    sortState,
    {
      field:
        'data_vencimento',

      order:
        'desc'
    }
  );

  executarNaPrimeiraPagina();
}

function ordenar(field) {
  if (sortState.field === field) {
    sortState.order =
      sortState.order === 'asc'
        ? 'desc'
        : 'asc';
  } else {
    sortState.field = field;
    sortState.order = 'asc';
  }

  executarNaPrimeiraPagina();
}

function sortArrow(field) {
  if (sortState.field !== field) {
    return '';
  }

  return sortState.order === 'asc'
    ? '▲'
    : '▼';
}

function podeRegistrarItem(item) {
  return STATUS_REGISTRAVEIS.has(
    String(item?.status || '')
  );
}

function estaProcessando(id) {
  return processandoIds.value.has(
    Number(id)
  );
}

function definirProcessando(
  id,
  ativo
) {
  const next =
    new Set(
      processandoIds.value
    );

  const normalizedId =
    Number(id);

  if (ativo) {
    next.add(normalizedId);
  } else {
    next.delete(normalizedId);
  }

  processandoIds.value = next;
}

function alternarSelecaoPagina(event) {
  if (event?.target?.checked) {
    selecionados.value =
      itensRegistraveis.value.map(
        (item) =>
          Number(item.id)
      );

    return;
  }

  selecionados.value = [];
}

async function mostrarAlerta(
  mensagem,
  titulo
) {
  if (proxy?.$alert) {
    await proxy.$alert(
      mensagem,
      titulo
    );

    return;
  }

  window.alert(
    `${titulo}: ${mensagem}`
  );
}

async function registrarId(
  id
) {
  definirProcessando(
    id,
    true
  );

  try {
    const resultado =
      await cobrancasApi.registrar(
        id
      );

    return {
      id: Number(id),
      sucesso: true,
      registrada:
        resultado?.registrada === true,
      idempotente:
        resultado?.idempotente === true,
      erro: null
    };
  } catch (error) {
    console.error(
      `Erro ao registrar cobrança #${id}:`,
      error
    );

    return {
      id: Number(id),
      sucesso: false,
      registrada: false,
      idempotente: false,
      erro:
        apiErrorMessage(
          error,
          'Falha não identificada.'
        )
    };
  } finally {
    definirProcessando(
      id,
      false
    );
  }
}

async function registrarItem(item) {
  if (
    !podeOperar.value ||
    !podeRegistrarItem(item) ||
    operacaoEmAndamento.value
  ) {
    return;
  }

  const confirmou =
    window.confirm(
      `Registrar a cobrança #${item.id}?

` +
      'Esta ação enviará a cobrança ao provedor configurado.'
    );

  if (!confirmou) {
    return;
  }

  const resultado =
    await registrarId(
      item.id
    );

  await carregarCobrancas();

  if (resultado.sucesso) {
    await mostrarAlerta(
      resultado.idempotente
        ? 'A cobrança consta como registrada.'
        : 'Cobrança registrada com sucesso.',
      'Cobrança'
    );

    return;
  }

  await mostrarAlerta(
    resultado.erro,
    'Erro'
  );
}

async function registrarSelecionadas() {
  if (
    !podeOperar.value ||
    processandoLote.value ||
    selecionados.value.length === 0
  ) {
    return;
  }

  const ids =
    selecionados.value
      .map(Number)
      .filter((id) =>
        itensRegistraveis.value.some(
          (item) =>
            Number(item.id) === id
        )
      );

  if (!ids.length) {
    selecionados.value = [];
    return;
  }

  const confirmou =
    window.confirm(
      `Registrar ${ids.length} cobrança(s) selecionada(s)?

` +
      'Cada cobrança será processada separadamente. Uma falha não interromperá as demais.'
    );

  if (!confirmou) {
    return;
  }

  processandoLote.value = true;

  const resultados = [];

  try {
    for (const id of ids) {
      resultados.push(
        await registrarId(id)
      );
    }
  } finally {
    processandoLote.value = false;
    selecionados.value = [];
  }

  await carregarCobrancas();

  const sucessos =
    resultados.filter(
      (resultado) =>
        resultado.sucesso
    );

  const registradas =
    sucessos.filter(
      (resultado) =>
        resultado.registrada
    ).length;

  const idempotentes =
    sucessos.filter(
      (resultado) =>
        resultado.idempotente
    ).length;

  const falhas =
    resultados.filter(
      (resultado) =>
        !resultado.sucesso
    );

  const linhas = [
    `Processadas: ${resultados.length}`,
    `Registradas: ${registradas}`,
    `Constam como registradas: ${idempotentes}`,
    `Falhas: ${falhas.length}`
  ];

  if (falhas.length) {
    linhas.push(
      '',
      'Falhas:'
    );

    for (const falha of falhas.slice(0, 8)) {
      linhas.push(
        `#${falha.id}: ${falha.erro}`
      );
    }

    if (falhas.length > 8) {
      linhas.push(
        `... e mais ${falhas.length - 8} falha(s).`
      );
    }
  }

  await mostrarAlerta(
    linhas.join('\n'),
    falhas.length
      ? 'Registro concluído com alertas'
      : 'Registro concluído'
  );
}

function detalhar(id) {
  router.push(
    `/financeiro/cobrancas/${id}/detalhar`
  );
}

function formatMoney(value) {
  return globalUtils.formatarValor(
    Number(value ?? 0)
  );
}

function formatDate(value) {
  return (
    globalUtils.formatarData(
      value
    ) ||
    '—'
  );
}

function formatInstrument(value) {
  return value === 'BOLETO_PIX'
    ? 'Boleto + PIX'
    : value === 'BOLETO'
      ? 'Boleto'
      : value || '—';
}

function statusLabel(value) {
  return (
    statusLabels[value] ||
    String(value || '—')
      .replaceAll('_', ' ')
  );
}

function statusClass(value) {
  const status =
    String(value || '');

  if (
    [
      'REGISTRADA',
      'LIQUIDADA',
      'CREDITADA'
    ].includes(status)
  ) {
    return 'status-success';
  }

  if (
    [
      'RASCUNHO',
      'REGISTRO_PENDENTE',
      'ALTERACAO_PENDENTE',
      'BAIXA_PENDENTE'
    ].includes(status)
  ) {
    return 'status-warning';
  }

  if (
    [
      'REJEITADA',
      'VENCIDA',
      'ESTORNADA',
      'CANCELADA',
      'ERRO'
    ].includes(status)
  ) {
    return 'status-danger';
  }

  if (
    [
      'BAIXADA',
      'SUBSTITUIDA'
    ].includes(status)
  ) {
    return 'status-secondary';
  }

  return 'status-info';
}

watch(
  paginaAtual,
  () => {
    carregarCobrancas();
  }
);

watch(
  limitePorPagina,
  () => {
    executarNaPrimeiraPagina();
  }
);

watch(
  () => filters.busca,
  () => {
    window.clearTimeout(
      searchTimer
    );

    searchTimer =
      window.setTimeout(
        aplicarFiltros,
        400
      );
  }
);

onMounted(
  async () => {
    await fetchPermissions();

    if (
      !hasPermission(
        'financeiro_cobrancas_visualizar'
      )
    ) {
      await proxy?.$alert?.(
        'Você não possui permissão para visualizar cobranças.',
        'Permissão'
      );

      router.replace(
        '/dashboard'
      );

      return;
    }

    await carregarContas();
    await carregarCobrancas();
  }
);

onBeforeUnmount(
  () => {
    window.clearTimeout(
      searchTimer
    );

    requestSequence += 1;
  }
);
</script>

<style scoped>
.cobrancas-page {
  min-width: 0;
}

.filters-grid {
  display: grid;
  grid-template-columns:
    minmax(240px, 2fr)
    repeat(5, minmax(145px, 1fr))
    auto;
  gap: 0.75rem;
  align-items: end;
}

.filter-actions {
  display: flex;
}

.filters-grid > div {
  min-width: 0;
}

.bulk-actions {
  border-left: 0.25rem solid #0d6efd;
}

.selection-column {
  width: 2.75rem;
  min-width: 2.75rem;
}

.form-label {
  margin-bottom: 0.25rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #495057;
}

.sort-button {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font-weight: 600;
  white-space: nowrap;
}

.sort-button:hover {
  color: #0d6efd;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.45rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  white-space: nowrap;
}

.status-success {
  color: #146c43;
  background: #d1e7dd;
}

.status-warning {
  color: #664d03;
  background: #fff3cd;
}

.status-danger {
  color: #842029;
  background: #f8d7da;
}

.status-secondary {
  color: #41464b;
  background: #e2e3e5;
}

.status-info {
  color: #055160;
  background: #cff4fc;
}

.page-limit {
  width: 6rem;
}

:deep(.pagination.pagination-sm .page-link) {
  line-height: 1;
  font-size: 0.8rem !important;
  border-radius: 0 !important;
}

.table th,
.table td {
  font-size: 0.82rem;
}

@media (max-width: 1400px) {
  .filters-grid {
    grid-template-columns:
      repeat(3, minmax(0, 1fr));
  }

  .filter-wide {
    grid-column: span 2;
  }
}

@media (max-width: 1000px) {
  .filters-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .filters-grid {
    grid-template-columns: 1fr;
  }

  .filter-wide {
    grid-column: auto;
  }
}
</style>
